import { useRouter } from "@core/Api/useRouter";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { revisionCatalogStore } from "@ext/git/actions/Revisions/logic/store/RevisionCatalogStore";
import { disableLiveDiff, useLiveDiffAttached } from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { useCallback, useEffect, useRef } from "react";

const isRevisionCatalogPathname = (pathname: string) => pathname.includes("commit-") || pathname.includes("dif-");

const toPagePath = (pathname: string) => decodeURIComponent(pathname).replace(/^\/+|\/+$/g, "");

/**
 * Opens a revision in the catalog at its commit; the newest revision is the current catalog itself. The revision
 * shows as opening until the page shows another catalog or article, or until no page is going to open.
 */
export const useGotoRevision = () => {
	const router = useRouter();
	const attached = useLiveDiffAttached();
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);
	const openingRef = useRef<string>(null);

	const finishOpening = useCallback(() => {
		const opening = openingRef.current;
		openingRef.current = null;
		const store = revisionCatalogStore.getState();
		if (opening && store.openingRevision === opening) store.setOpeningRevision(null);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the page that shows another catalog or article is the opened revision
	useEffect(() => finishOpening, [catalogName, articlePath, finishOpening]);

	const { call: gotoRevision } = useDeferApi<string, string>({
		onDone: (pathname) => {
			const samePage = toPagePath(pathname) === toPagePath(router.path);
			if (isRevisionCatalogPathname(pathname)) void router.pushPath(pathname, { diff: "1" });
			else if (attached) disableLiveDiff(router);
			else void router.pushPath(pathname);

			if (samePage || (attached && !isRevisionCatalogPathname(pathname))) finishOpening();
		},
		onFinally: (pathname) => {
			if (!pathname) finishOpening();
		},
	});

	return useCallback(
		(commitOid: string, oldCommitOid?: string) => {
			openingRef.current = commitOid;
			revisionCatalogStore.getState().setOpeningRevision(commitOid);
			return gotoRevision({ url: (api) => api.getPathnameToRevision(commitOid, oldCommitOid) });
		},
		[gotoRevision],
	);
};
